import { observer } from 'mobx-react-lite';

import setAttachmentShouldShowContextMenu from 'owa-attachment-full-data/lib/actions/setAttachmentShouldShowContextMenu';
import setShouldShowActionsContainer from 'owa-attachment-full-data/lib/actions/setShouldShowActionsContainer';
import { ConversationAttachmentWellViewState } from 'owa-attachment-well-data';
import updateConversationAttachmentWellMaxHeight from 'owa-attachment-well-data/lib/mutators/updateConversationAttachmentWellMaxHeight';
import { createImageGalleryForNewSxS } from 'owa-attachment-well-preview-actions';
import { InstrumentationContext } from 'owa-mail-store';
import { lazySubscribeToResizeEvent, lazyUnsubscribeFromResizeEvent } from 'owa-resize-event';
import * as React from 'react';
import ConversationAttachmentWellItem from './ConversationAttachmentWellItem';

export interface ConversationAttachmentWellProps {
    viewState: ConversationAttachmentWellViewState;
    instrumentationContext: InstrumentationContext;
    onBeforePreview?: () => void;
}

const styles = require('./ConversationAttachmentWell.scss');
const classNames = require('classnames/bind').bind(styles);

export default observer(function ConversationAttachmentWell(
    props: ConversationAttachmentWellProps
) {
    React.useEffect(() => {
        onResize();
        lazySubscribeToResizeEvent.importAndExecute(onResize);
        return () => {
            lazyUnsubscribeFromResizeEvent.importAndExecute(onResize);
        };
    }, []);
    const onItemFocused = (focusedAttachmentId: string) => {
        props.viewState.docViewAttachments.forEach(attachmentViewState => {
            setShouldShowActionsContainer(
                attachmentViewState,
                attachmentViewState.attachmentId.Id === focusedAttachmentId
            );
            if (
                attachmentViewState.shouldShowContextMenu &&
                attachmentViewState.attachmentId.Id !== focusedAttachmentId
            ) {
                setAttachmentShouldShowContextMenu(attachmentViewState, false);
            }
        });
    };
    const onResize = () => {
        updateConversationAttachmentWellMaxHeight(props.viewState);
    };
    const createImageGallery = async () => {
        return createImageGalleryForNewSxS(props.viewState, true);
    };

    let focusInitialized: boolean = false;
    let beforeElement: HTMLDivElement = null;
    let afterElement: HTMLDivElement = null;
    const focusLastElement = () => {
        if (focusInitialized) {
            const lastElement = afterElement?.previousElementSibling as HTMLDivElement;
            lastElement?.focus();
            onItemFocused(
                props.viewState.docViewAttachments[props.viewState.docViewAttachments.length - 1]
                    .attachmentId.Id
            );
        } else {
            focusInitialized = true;
            focusFirstElement();
        }
    };
    const focusFirstElement = () => {
        const firstElement = beforeElement?.nextElementSibling as HTMLDivElement;
        firstElement?.focus();
        onItemFocused(props.viewState.docViewAttachments[0].attachmentId.Id);
    };

    return (
        <div
            className={classNames('disableTextSelection', 'customScrollBar', styles.container)}
            style={{ maxHeight: props.viewState.maxHeight + 'px' }}>
            <div ref={ref => (beforeElement = ref)} tabIndex={0} onFocus={focusLastElement} />
            {props.viewState.docViewAttachments.map(attachmentViewState => (
                <ConversationAttachmentWellItem
                    key={'convoWell_' + attachmentViewState.attachmentId.Id}
                    attachmentViewState={attachmentViewState}
                    parentItemId={
                        props.viewState.parentItemIdMap[attachmentViewState.attachmentId.Id]
                    }
                    itemReceivedTime={
                        props.viewState.itemReceivedTimeMap[attachmentViewState.attachmentId.Id]
                    }
                    instrumentationContext={props.instrumentationContext}
                    onBeforePreview={props.onBeforePreview}
                    onFocus={onItemFocused}
                    createImageGallery={createImageGallery}
                />
            ))}
            <div ref={ref => (afterElement = ref)} tabIndex={0} onFocus={focusFirstElement} />
        </div>
    );
});
